import { useState, useEffect, useRef } from "react";

// @mui material components
import {
  Grid,
  TextField,
  Box,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";

import { CustomerStatus } from "../../model";
import { DRAWER_FORM_GRID_SPACING } from "../../../../lib/ui/dialogFormLayout";
import { DrawerForm, DrawerFormSection } from "../../../../components/DrawerForm";
import { FormHiddenField, IconAutocomplete } from "../../../../components/forms";
import { CUSTOMER_STATUS_OPTIONS } from "../../../../lib/ui/iconSelectOptions";
import { normalizeCustomerUsers } from "../../../../lib/customer/users";
import { notifyValidationWarning } from "../../../../utils/notification";
import CustomerUsersField from "./CustomerUsersField";
import CustomerPhoneField from "./CustomerPhoneField";
import { formatCustomerPhone } from "../../../../lib/customer/phone";
import LocalizationFields from "../../../../components/forms/LocalizationFields";
import { useTranslation } from "react-i18next";

const EditCustomerDialogBody = (item: any) => {
  const { t } = useTranslation();
  const { sharedLogoBase64, sharedLogoPreview, onLogoChange, ...customerData } = item;
  
  const [name, setName] = useState<string>('');
  const [users, setUsers] = useState<string[]>([]);
  const [description, setDescription] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [mobilePhone, setMobilePhone] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [pointOfContact, setPointOfContact] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(CustomerStatus.ACTIVE);
  const [startWorkTime, setStartWorkTime] = useState<string>('09:00');
  const [endWorkTime, setEndWorkTime] = useState<string>('17:00');
  const [timeZone, setTimeZone] = useState<string>('Europe/Istanbul');
  const [locale, setLocale] = useState<string>('tr-TR');
  
  // Use external logo state if provided
  const logoPreview = sharedLogoPreview !== undefined ? sharedLogoPreview : null;
  const logoBase64 = sharedLogoBase64 !== undefined ? sharedLogoBase64 : '';

  const sharedLogoPreviewRef = useRef(sharedLogoPreview);
  sharedLogoPreviewRef.current = sharedLogoPreview;

  // Sync from server props using stable primitives — `customerData` as a dep would be a new
  // object every parent render and would reset fields (e.g. username) while the user types.
  useEffect(() => {
    if (!customerData?.id) return;

    setName(customerData.name ?? '');
    const nextUsers = normalizeCustomerUsers(customerData.users ?? customerData.username);
    setUsers(nextUsers.length > 0 ? nextUsers : [""]);
    setDescription(customerData.description ?? '');
    setEmail(customerData.email ?? '');
    setPhone(formatCustomerPhone(customerData.phone ?? ''));
    setMobilePhone(formatCustomerPhone(customerData.mobilePhone ?? ''));
    setAddress(customerData.address ?? '');
    setPointOfContact(customerData.pointOfContact ?? '');
    setSelectedStatus(customerData.status || CustomerStatus.ACTIVE);
    setStartWorkTime(customerData.startWorkTime || '09:00');
    setEndWorkTime(customerData.endWorkTime || '17:00');
    setTimeZone(customerData.timeZone || 'Europe/Istanbul');
    setLocale(customerData.locale || 'tr-TR');
  }, [
    customerData.id,
    customerData.name,
    customerData.users,
    customerData.username,
    customerData.description,
    customerData.email,
    customerData.phone,
    customerData.mobilePhone,
    customerData.address,
    customerData.pointOfContact,
    customerData.status,
    customerData.startWorkTime,
    customerData.endWorkTime,
    customerData.timeZone,
    customerData.locale,
  ]);

  useEffect(() => {
    if (!customerData?.id || !customerData.logo || !onLogoChange) return;
    if (sharedLogoPreviewRef.current) return;

    const logoData = customerData.logo.startsWith('data:')
      ? customerData.logo
      : `data:image/png;base64,${customerData.logo}`;
    const base64Only = customerData.logo.includes(',')
      ? customerData.logo.split(',')[1]
      : customerData.logo;
    onLogoChange(base64Only, logoData);
  }, [customerData.id, customerData.logo, onLogoChange]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith("image/")) {
        notifyValidationWarning("Invalid file", "Please select an image file.");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        notifyValidationWarning("File too large", "Logo must be smaller than 5 MB.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          // Remove data:image/...;base64, prefix
          const base64String = result.split(',')[1];
          // Update external state if callback provided
          if (onLogoChange) {
            onLogoChange(base64String, result);
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    if (onLogoChange) {
      onLogoChange('', null);
    }
  };

  return (
    <DrawerForm>
      <FormHiddenField name="id" value={customerData?.id || ""} />

      <DrawerFormSection title={t("form.identity")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              id="name"
              name="name"
              label={t("common.name")}
              variant="outlined"
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              id="description"
              name="description"
              label={t("common.description")}
              multiline
              rows={2}
              variant="outlined"
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="logo-upload-edit"
                type="file"
                onChange={handleFileUpload}
              />
              {logoPreview ? (
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo preview"
                  sx={{ width: 32, height: 32, objectFit: "contain", borderRadius: "4px", border: 1, borderColor: "divider" }}
                />
              ) : null}
              <Button component="label" htmlFor="logo-upload-edit" size="small" variant="outlined" sx={{ textTransform: "none" }}>
                {logoPreview ? "Change logo" : "Upload logo"}
              </Button>
              {logoPreview ? (
                <IconButton onClick={removeLogo} color="error" size="small" aria-label={t("customers.removeLogo")}>
                  <Icon>close</Icon>
                </IconButton>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Optional
                </Typography>
              )}
            </Stack>
            <FormHiddenField name="logo" value={logoBase64} />
            <FormHiddenField name="sourceLogo" value={logoBase64} />
          </Grid>
        </Grid>
      </DrawerFormSection>

      <CustomerUsersField users={users} onChange={setUsers} />

      <DrawerFormSection title={t("form.contact")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={6}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label={t("common.email")}
              type="email"
              variant="outlined"
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <CustomerPhoneField
              id="phone"
              name="phone"
              label={t("common.phone")}
              value={phone}
              onChange={setPhone}
            />
          </Grid>
          <Grid size={6}>
            <CustomerPhoneField
              id="mobilePhone"
              name="mobilePhone"
              label={t("common.mobilePhone")}
              value={mobilePhone}
              onChange={setMobilePhone}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              id="pointOfContact"
              name="pointOfContact"
              label={t("customers.pointOfContact")}
              variant="outlined"
              size="small"
              value={pointOfContact}
              onChange={(e) => setPointOfContact(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              id="address"
              name="address"
              label={t("common.address")}
              multiline
              rows={2}
              variant="outlined"
              size="small"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </Grid>
        </Grid>
      </DrawerFormSection>

      <DrawerFormSection title={t("form.workingHours")}>
        <Grid container spacing={DRAWER_FORM_GRID_SPACING}>
          <Grid size={12}>
            <IconAutocomplete
              options={CUSTOMER_STATUS_OPTIONS}
              value={selectedStatus}
              onChange={(value) => setSelectedStatus(value || CustomerStatus.ACTIVE)}
              label={t("common.status")}
              required
              disableClearable
              noOptionsText="No status options found"
            />
            <FormHiddenField name="status" value={selectedStatus || CustomerStatus.ACTIVE} />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              id="startWorkTime"
              name="startWorkTime"
              label={t("customers.start")}
              type="time"
              variant="outlined"
              size="small"
              value={startWorkTime}
              onChange={(e) => setStartWorkTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              id="endWorkTime"
              name="endWorkTime"
              label={t("customers.end")}
              type="time"
              variant="outlined"
              size="small"
              value={endWorkTime}
              onChange={(e) => setEndWorkTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
          </Grid>
          <LocalizationFields
            timeZone={timeZone}
            locale={locale}
            onTimeZoneChange={setTimeZone}
            onLocaleChange={setLocale}
          />
        </Grid>
      </DrawerFormSection>
    </DrawerForm>
  );
};

export default EditCustomerDialogBody;

